import { jwtDecode } from 'jwt-decode';

import type { IJwtPayload } from './types';
import { LocalStorage } from './storage';

/**
 * Retrieves the stored organization ID for a specific user
 * @param userId - The user ID to look up
 * @returns The stored organization ID or null if not found
 */
export const getStoredOrgIdForUser = (userId: string): string | null => {
  return LocalStorage.get(`${userId}_org`);
};

/**
 * Stores the organization ID for a specific user
 * @param userId - The user ID to store the org for
 * @param orgId - The organization ID to store
 */
export const setStoredOrgIdForUser = (userId: string, orgId: string): void => {
  LocalStorage.set(`${userId}_org`, orgId);
};

/**
 * Resolves the appropriate organization ID from a JWT token.
 *
 * Logic:
 * 1. First checks if user has a stored org preference
 * 2. If stored org exists in token, uses that
 * 3. Otherwise, uses the first org from token and stores it
 *
 * @param token - JWT token to parse
 * @returns The resolved organization ID or null if none found
 */
export const resolveOrgFromToken = (token: string): string | null => {
  try {
    const p = jwtDecode<IJwtPayload>(token);
    if (!p?.sub) return null;

    const storedOrgId = getStoredOrgIdForUser(p.sub);
    if (storedOrgId) {
      const orgInToken = p.organizations?.find(org => org.id === storedOrgId);
      if (orgInToken) {
        return storedOrgId;
      }
    }

    if (p.organizations?.length > 0) {
      const firstOrgId = p.organizations[0].id;
      setStoredOrgIdForUser(p.sub, firstOrgId);
      return firstOrgId;
    }

    return null;
  } catch (error) {
    console.error('Error resolving org from token:', error);
    return null;
  }
};
